/**
 * Gate pass-through break effect + floating count popup.
 *
 * On contact: gate shatters/dissolves with a particle burst (`gateBreak`)
 * and a pooled floating `+N` / `×M` / `−N` delta. Presentation only —
 * sim already applied the math; this layer juiced the choice.
 */
import * as THREE from "three";
/** Max simultaneous shatter shards (pooled). */
export const GATE_BREAK_SHARD_POOL = 48;
/** Max simultaneous count popup sprites (pooled). */
export const GATE_BREAK_POPUP_POOL = 8;
/** Shatter / dissolve lifetime (seconds). */
export const GATE_BREAK_LIFE = 0.55;
/** Count popup float lifetime (seconds). */
export const GATE_POPUP_LIFE = 0.85;
/** Format operator text for gate labels and popups. */
export function formatGateOpText(op, k) {
    const K = Math.floor(k);
    switch (op) {
        case "add":
            return `+${K}`;
        case "mul":
            return `×${K}`;
        case "sub":
            return `−${K}`;
        case "div":
            return `÷${K}`;
        default: {
            const _e = op;
            return String(_e);
        }
    }
}
/** Gate body / glow color: green for + / ×, red for −, amber for ÷. */
export function gateOpColor(op) {
    switch (op) {
        case "add":
        case "mul":
            return 0x34d399;
        case "sub":
            return 0xf87171;
        case "div":
            return 0xfbbf24;
        default: {
            const _e = op;
            return _e;
        }
    }
}
function makeOpTexture(text, colorHex, size = 256) {
    if (typeof document === "undefined") {
        const data = new Uint8Array([255, 255, 255, 255]);
        const tex = new THREE.DataTexture(data, 1, 1);
        tex.needsUpdate = true;
        return tex;
    }
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        ctx.clearRect(0, 0, size, size);
        // Soft glow plate so text reads on busy bridge
        ctx.fillStyle = "rgba(15, 23, 42, 0.55)";
        const pad = size * 0.08;
        roundRect(ctx, pad, size * 0.28, size - pad * 2, size * 0.44, size * 0.08);
        ctx.fill();
        ctx.font = `bold ${Math.floor(size * 0.38)}px system-ui,Segoe UI,sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.lineWidth = Math.max(4, size * 0.04);
        ctx.strokeStyle = "rgba(0,0,0,0.75)";
        ctx.strokeText(text, size / 2, size / 2);
        ctx.fillStyle = colorHex;
        ctx.fillText(text, size / 2, size / 2);
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.needsUpdate = true;
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}
function roundRect(ctx, x, y, w, h, r) {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
}
function hexCss(hex) {
    return `#${hex.toString(16).padStart(6, "0")}`;
}
/**
 * Create pooled gateBreak shatter + count-popup FX.
 * Name / group id intentionally include `gateBreak` for campaign verify.
 */
export function createGateBreakFx() {
    const group = new THREE.Group();
    group.name = "gateBreakFx";
    // --- shatter shard pool (instanced boxes) ---
    const shardGeo = new THREE.BoxGeometry(0.28, 0.22, 0.12);
    const shardMat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.95,
        depthWrite: false,
    });
    const shardMesh = new THREE.InstancedMesh(shardGeo, shardMat, GATE_BREAK_SHARD_POOL);
    shardMesh.name = "gateBreakShards";
    shardMesh.count = 0;
    shardMesh.frustumCulled = false;
    shardMesh.matrixAutoUpdate = false;
    shardMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // Per-instance color support
    shardMesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(GATE_BREAK_SHARD_POOL * 3), 3);
    group.add(shardMesh);
    const shards = [];
    for (let i = 0; i < GATE_BREAK_SHARD_POOL; i++) {
        shards.push({
            active: false,
            age: 0,
            life: GATE_BREAK_LIFE,
            x: 0,
            y: 0,
            z: 0,
            vx: 0,
            vy: 0,
            vz: 0,
            sx: 1,
            sy: 1,
            sz: 1,
            rot: 0,
            spin: 0,
            color: 0x34d399,
        });
    }
    let shardCursor = 0;
    // --- count popup pool (sprites, recycled) ---
    const popups = [];
    for (let i = 0; i < GATE_BREAK_POPUP_POOL; i++) {
        const material = new THREE.SpriteMaterial({
            transparent: true,
            depthWrite: false,
            opacity: 0,
        });
        const sprite = new THREE.Sprite(material);
        sprite.name = `gateBreakPopup:${i}`;
        sprite.visible = false;
        sprite.scale.set(1.8, 1.8, 1);
        group.add(sprite);
        popups.push({
            active: false,
            age: 0,
            life: GATE_POPUP_LIFE,
            x: 0,
            y: 0,
            z: 0,
            sprite,
            material,
            texture: null,
        });
    }
    let popupCursor = 0;
    const dummy = new THREE.Object3D();
    const colorTmp = new THREE.Color();
    const spawnShards = (x, y, z, color, count) => {
        const n = Math.min(GATE_BREAK_SHARD_POOL, Math.max(8, count));
        for (let i = 0; i < n; i++) {
            const s = shards[shardCursor];
            shardCursor = (shardCursor + 1) % GATE_BREAK_SHARD_POOL;
            const a = (i / n) * Math.PI * 2 + shardCursor * 0.11;
            const elev = 0.35 + (i % 5) * 0.18;
            const speed = 2.2 + (i % 4) * 0.55;
            s.active = true;
            s.age = 0;
            s.life = GATE_BREAK_LIFE * (0.75 + (i % 4) * 0.08);
            s.x = x + Math.cos(a) * 0.15;
            s.y = y + (i % 3) * 0.12;
            s.z = z + Math.sin(a) * 0.1;
            s.vx = Math.cos(a) * speed;
            s.vy = elev * 3.2 + (i % 3) * 0.4;
            s.vz = Math.sin(a) * speed * 0.85;
            s.sx = 0.7 + (i % 3) * 0.25;
            s.sy = 0.6 + (i % 4) * 0.2;
            s.sz = 0.5 + (i % 2) * 0.3;
            s.rot = a;
            s.spin = (i % 2 === 0 ? 1 : -1) * (8 + (i % 5));
            s.color = color;
        }
    };
    const spawnPopup = (x, y, z, op, k, color) => {
        const p = popups[popupCursor];
        popupCursor = (popupCursor + 1) % GATE_BREAK_POPUP_POOL;
        if (p.texture) {
            p.texture.dispose();
            p.texture = null;
        }
        const text = formatGateOpText(op, k);
        const tex = makeOpTexture(text, hexCss(color), 256);
        p.texture = tex;
        p.material.map = tex;
        p.material.opacity = 1;
        p.material.needsUpdate = true;
        p.active = true;
        p.age = 0;
        p.life = GATE_POPUP_LIFE;
        p.x = x;
        p.y = y + 0.6;
        p.z = z;
        p.sprite.position.set(p.x, p.y, p.z);
        p.sprite.scale.set(2.1, 2.1, 1);
        p.sprite.visible = true;
    };
    const triggerGateBreak = (input) => {
        const y = input.y ?? 1.0;
        const color = input.color ?? gateOpColor(input.op);
        // Core shatter + dissolve burst
        spawnShards(input.x, y, input.z, color, 18);
        // Floating count delta (+N / ×M / −N)
        spawnPopup(input.x, y, input.z, input.op, input.k, color);
        group.visible = true;
    };
    const update = (dtSeconds) => {
        const dt = Math.max(0, Math.min(0.05, dtSeconds));
        if (dt <= 0)
            return;
        let anyShard = false;
        let drawn = 0;
        for (let i = 0; i < GATE_BREAK_SHARD_POOL; i++) {
            const s = shards[i];
            if (!s.active)
                continue;
            s.age += dt;
            if (s.age >= s.life) {
                s.active = false;
                continue;
            }
            anyShard = true;
            const t = s.age;
            s.x += s.vx * dt;
            s.y += s.vy * dt;
            s.z += s.vz * dt;
            s.vy -= 9.5 * dt;
            s.rot += s.spin * dt;
            const fade = 1 - s.age / s.life;
            const scale = (0.55 + 0.45 * fade) * s.sx;
            dummy.position.set(s.x, s.y, s.z);
            dummy.rotation.set(s.rot, s.rot * 0.7, s.rot * 0.4);
            dummy.scale.set(scale, scale * (s.sy / s.sx), scale * (s.sz / s.sx));
            dummy.updateMatrix();
            shardMesh.setMatrixAt(drawn, dummy.matrix);
            colorTmp.setHex(s.color);
            // Dim with life
            colorTmp.multiplyScalar(0.45 + 0.55 * fade);
            shardMesh.setColorAt(drawn, colorTmp);
            drawn += 1;
        }
        shardMesh.count = drawn;
        shardMesh.instanceMatrix.needsUpdate = true;
        if (shardMesh.instanceColor) {
            shardMesh.instanceColor.needsUpdate = true;
        }
        // Shared material opacity follows average fade of active shards
        shardMat.opacity = anyShard ? 0.9 : 0;
        let anyPopup = false;
        for (let i = 0; i < GATE_BREAK_POPUP_POOL; i++) {
            const p = popups[i];
            if (!p.active)
                continue;
            p.age += dt;
            if (p.age >= p.life) {
                p.active = false;
                p.sprite.visible = false;
                p.material.opacity = 0;
                continue;
            }
            anyPopup = true;
            const u = p.age / p.life;
            // Float up + ease out
            p.y += (1.8 + (1 - u) * 0.6) * dt;
            p.sprite.position.set(p.x, p.y, p.z);
            const fade = u < 0.15 ? u / 0.15 : 1 - (u - 0.15) / 0.85;
            p.material.opacity = Math.max(0, Math.min(1, fade));
            const sc = 2.1 + u * 0.55;
            p.sprite.scale.set(sc, sc, 1);
        }
        group.visible = anyShard || anyPopup;
    };
    const reset = () => {
        for (const s of shards)
            s.active = false;
        for (const p of popups) {
            p.active = false;
            p.sprite.visible = false;
            p.material.opacity = 0;
            if (p.texture) {
                p.texture.dispose();
                p.texture = null;
                p.material.map = null;
            }
        }
        shardMesh.count = 0;
        group.visible = false;
    };
    const dispose = () => {
        reset();
        shardGeo.dispose();
        shardMat.dispose();
        for (const p of popups) {
            p.material.dispose();
        }
        group.clear();
    };
    return {
        group,
        triggerGateBreak,
        update,
        reset,
        dispose,
    };
}
//# sourceMappingURL=gateBreak.js.map